import React from "react";

export const PrivacyPolicyText = () => {
  return (
    <div className="leading-7">
      <h1>U.GG Privacy Policy</h1>
      <p className="mb-7">Last Updated on January 16, 2026</p>

      <h2>Introduction</h2>
      <p className="mb-7">
        This privacy policy (the “Privacy Policy”) will help you understand what information Outplayed Inc. and its various
        platforms (such as U.GG and probuildstats.com) collects and uses, and the choices you have associated with that
        information. When we refer to “Outplayed,” “we,” “our,” “us” in this policy, we are referring to Outplayed, Inc., which
        provides the Services to you. The “Services” refers to the applications, services, and websites (marketing and product)
        provided by Outplayed. Outplayed may, from time to time, introduce new products and services. To the extent that these new
        products and services affect this policy, we will notify you as described in Section 14 below. This privacy policy covers
        U.GG and all of its subdomains. The use of information collected through our service shall be limited to the purpose of
        providing the services for our customers and as specified herein.
      </p>

      <p className="mb-8">
        We value the privacy of those who visit and/or use the Services (individually, “you” or collectively, “Users”). We take
        your privacy seriously and make it a priority to protect personal information that we obtain from and about our Users.
      </p>

      <p className="mb-7">
        Although the information in this Privacy Policy is generally intended to apply to all visitors and users of our sites,
        certain sections only apply to certain individuals. In this respect, residents of California should consult the section
        titled “Additional Data Rights Applicable to California Residents” for rights that apply to them, and residents of the
        European Economic Area (“EEA”), which includes the member states of the European Union (“EU”), and residents of the United
        Kingdom and Switzerland, should consult the sections of this policy relating to the “EU-U.S. Data Privacy Framework” and
        “Additional Data Rights Applicable to EEA Residents” for provisions that may apply to them.
      </p>

      <p className="mb-7">
        By visiting and/or using the Services you expressly consent to the information handling practices described in this
        Privacy Policy. If you do not want your personal information to be used in the manner set forth in this Privacy Policy,
        you must not use the Services and shall cease using the Services immediately.
      </p>

      <p className="mb-7">
        Our collection and use of information that identifies you, or by which your identity could, alone or in combination with
        other information, be deduced (such as your name, address, telephone number and e-mail address) (“Personal Information”)
        is subject to the terms of this Privacy Policy.
      </p>

      <p className="mb-7">
        If you have any questions, inquiries, or complaints about this Privacy Policy or our practices, please contact us via
        email at <a href="mailto:privacy@u.gg">privacy@u.gg</a>.
      </p>

      <h2>Contents</h2>
      <ol>
        <li>
          <a href="#section1">Information We Collect and Receive</a>
        </li>
        <li>
          <a href="#section2">How We Use the Information We Collect and Receive</a>
        </li>
        <li>
          <a href="#section3">Analytics, Cookies and Other Web Site Technologies</a>
        </li>
        <li>
          <a href="#section4">Information Sharing and Accountability for Onward Transfers</a>
        </li>
        <li>
          <a href="#section5">Communications</a>
        </li>
        <li>
          <a href="#section6">Accessing Your Data</a>
        </li>
        <li>
          <a href="#section7">Security</a>
        </li>
        <li>
          <a href="#section8">Data Retention</a>
        </li>
        <li>
          <a href="#section9">Providing or Withdrawing your Consent</a>
        </li>
        <li>
          <a href="#section10">Internal Policies and Procedures</a>
        </li>
        <li>
          <a href="#section11">Our Commitment to Children's Privacy</a>
        </li>
        <li>
          <a href="#section12">Recourse, Enforcement, and Dispute Resolution</a>
        </li>
        <li>
          <a href="#section13">Additional Data Rights</a>
        </li>
        <li>
          <a href="#section14">Changes to this Statement/Contact Us</a>
        </li>
      </ol>

      <h2 id="section1">1. Information We Collect and Receive</h2>
      <p className="mb-7">We collect several different types of information to provide Services to you, including:</p>

      <h3 className="leading-7">User Account and Registration Data</h3>
      <p className="mb-7">
        This includes information you provide to create your account with us, and may include, first and last name, billing
        information, a password and a valid email address.
      </p>

      <h3 className="leading-7">Service Data (including Session and Usage data)</h3>
      <p className="mb-7">
        When you use our Services, we receive information generated through the use of the Service, either entered by you or
        others who use the Services with you (for example, League of Legends summoner name, favourite champions, etc.), or from
        the Service infrastructure itself, (for example, duration of session, connection information, etc.). We may also collect
        usage and log data about how the services are accessed and used, including information about the device you are using the
        Services on, IP addresses, location information, language settings, what operating system you are using, unique device
        identifiers and other diagnostic data to help us support the Services.
      </p>

      <h3 className="leading-7">Device Information</h3>
      <p className="mb-7">
        When you use our Services, we automatically collect information on the type of device you use, operating system version,
        and the device identifier (or "UDID").
      </p>

      <h2 id="section2">2. How We Use the Information We Collect and Receive</h2>
      <p className="mb-7">
        U.GG may access and use the data we collect as necessary, including: (a) to provide and maintain the Services; (b) to
        address and respond to service, security, and customer support issues; (c) to detect, prevent, or otherwise address fraud,
        security, unlawful, or technical issues; (d) as required by law; (e) to fulfill our contracts; (f) to improve and enhance
        the Services; (g) to provide analysis or valuable information back to our customers and Users; and (h) to manage business
        risks.
      </p>

      <p className="mb-7">Some specific examples of how we use the information:</p>
      <ul className="mb-7">
        <li>Create and administer your account.</li>
        <li>Send you reminder and update emails.</li>
        <li>Send you product updates, marketing communication, and service information.</li>
        <li>Respond to customer inquiries and support requests.</li>
        <li>Conduct research and analysis.</li>
        <li>Display content based upon your interests.</li>
        <li>
          Analyze data, including through automated systems and machine learning to improve our services and/or your experience.
        </li>
        <li>Provide you information about your use of the services and benchmarks, insights and suggestions for improvements.</li>
      </ul>

      <p className="mb-7">
        We may generate non-identifying and aggregate profiles from the information you provide during registration for our
        Services and through your use of our Services. Non-identifying and aggregate profiles are used to improve the quality of
        our Services and to develop new Services. Aggregated non-personally identifying information may be shared with third
        parties.
      </p>

      <h2 id="section3">3. Analytics, Cookies and Other Web Site Technologies</h2>
      <p className="mb-7">
        U.GG is continuously improving our websites and products through the use of various third party web analytics tools, which
        helps us understand how visitors use our websites, what they like and dislike, and where they may have problems. While we
        maintain ownership of this data, we do not share this type of data about individual Users with third parties.
      </p>

      <h3 className="leading-7">Geolocation and Other Data</h3>
      <p className="mb-7">
        We may utilize precise Geolocation data but only if you specifically opt-in to collection of that data in connection with
        a particular service. We also use information such as IP addresses to determine the general geographic locations areas of
        our visitors. The web beacons used in conjunction with these web analytics tools may gather data such as what browser or
        operating system a person uses, as well as, domain names, MIME types, and what content, products and services are reviewed
        or downloaded when visiting or registering for services at one of our websites or using one of our mobile applications.
      </p>

      <h3 className="leading-7">Google Analytics</h3>
      <p className="mb-7">
        We use Google Analytics as described in “How Google uses data when you use our partners' sites or apps.” You can prevent
        your data from being used by Google Analytics on websites by installing the Google Analytics opt-out browser add-on{" "}
        <a href="https://tools.google.com/dlpage/gaoptout">here</a>. We also employ IP address masking, a technique used to
        truncate IP addresses collected by Google Analytics and store them in an abbreviated form to prevent them from being
        traced back to individual Users.
      </p>

      <p className="mb-7">
        Portions of our website may also use Google Analytics for Display Advertisers including DoubleClick or Dynamic Remarketing
        which provide interest-based ads based on your visit to this or other websites. You can use Ads Settings to manage the
        Google ads you see and opt-out of interest-based ads.
      </p>

      <h3 className="leading-7">Tracking Technologies</h3>
      <p className="mb-7">
        U.GG uses cookies and similar tracking technologies to track User traffic patterns and hold certain registration
        information. Tracking technologies also used are beacons, tags and scripts to collect and track information and to improve
        and analyze our service. The Help menu on the menu bar of most browsers will tell you how to prevent your browser from
        accepting new cookies, how to have the browser notify you when you receive a new cookie and how to disable cookies
        altogether. You can still review the website if you choose to set your browser to refuse all cookies; however, you must
        enable cookies to establish an account and to install the Services.
      </p>

      <h3 className="leading-7">Social Media</h3>
      <p className="mb-7">
        Our sites may include social media features, such as Facebook, Google, and X “share” buttons. These features may collect
        your IP address, which page you are visiting on our site, and may set a cookie to enable the feature to function properly.
        These services can also authenticate your identity and provide you the option to share certain Personal Information with
        us such as your name and email address to expedite the login and signup process respectively. Your interactions with these
        features are governed by the privacy policy of the company providing them.
      </p>

      <h3 className="leading-7">Hotjar</h3>
      <p className="mb-7">
        We use Hotjar in order to better understand our Users' needs and to optimize this service and experience. Hotjar is a
        technology service that helps us better understand our Users’ experience (e.g. how much time they spend on which pages,
        which links they choose to click, what users do and don't like, etc.) and this enables us to build and maintain our
        Services with user feedback. Hotjar uses cookies and other technologies to collect data on our Users' behavior and their
        devices (in particular device's IP address (captured and stored only in anonymized form), device screen size, device type
        (unique device identifiers), browser information, geographic location (country only), preferred language used to display
        our website). Hotjar stores this information in a pseudonymized user profile. Neither Hotjar nor we will ever use this
        information to identify individual users or to match it with further data on an individual user. For further details,
        please see Hotjar's privacy policy by clicking on <a href="https://www.hotjar.com/legal/policies/privacy">this link</a>.
      </p>

      <p className="mb-7">
        You can opt-out to the creation of a user profile, Hotjar's storing of data about your usage of our site and Hotjar's use
        of tracking cookies on other websites by following this{" "}
        <a href="https://www.hotjar.com/legal/compliance/opt-out">opt-out link</a>.
      </p>

      <h3 className="leading-7">Playwire Ads</h3>
      <p className="mb-7">
        All or partial advertising on this Website or App is managed by Playwire LLC. If Playwire publisher advertising services
        are used, Playwire LLC may collect and use certain aggregated and anonymized data for advertising purposes. To learn more
        about the types of data collected, how data is used and your choices as a User, please visit{" "}
        <a href="https://www.playwire.com/privacy-policy">https://www.playwire.com/privacy-policy</a>.
      </p>

      <h2 id="section4">4. Information Sharing and Accountability for Onward Transfers</h2>
      <p className="mb-7">
        Ensuring your privacy is important to us. We do not share your Personal Information with third parties except as described
        in this privacy policy. We may share your Personal Information (a) with third party service providers; (b) with business
        partners; (c) with affiliated companies within our corporate structure; (d) as needed to respond to requests from courts,
        law enforcement agencies, regulatory agencies, and other public and government authorities, which may include authorities
        outside your country of residence; and (e) to protect our interests, including (i) if we believe in good faith, that it is
        appropriate or necessary to take precautions against liability, (ii) to help us collect a debt or enforce an obligation
        owed to us by you, or (iii) to protect against fraudulent, abusive, or unlawful uses. Third party service providers have
        access to Personal Information only as needed to perform their functions and they must process the Personal Information in
        accordance with this Privacy Policy.
      </p>

      <p className="mb-7">Examples of how we may share information with service providers include:</p>
      <ul className="mb-7">
        <li>Payment processing and fraud prevention.</li>
        <li>Providing customer support.</li>
        <li>Sending marketing communications.</li>
        <li>Conducting research and analysis.</li>
        <li>Providing cloud computing infrastructure.</li>
      </ul>

      <p className="mb-7">Examples of how we may disclose data for legal reasons include:</p>
      <ul className="mb-7">
        <li>
          As part of a merger, sale of company assets, financing or acquisition of all or a portion of our business by another
          company where customer information will be one of the transferred assets.
        </li>
        <li>
          As required by law, for example, to comply with a valid subpoena or other legal process; when we believe in good faith
          that disclosure is necessary to protect our rights, or to protect your safety (or the safety of others); to investigate
          fraud; or to respond to a government request.
        </li>
      </ul>

      <p className="mb-7">
        We may also disclose your Personal Information to any third party with your prior consent. We do not share Personal
        Information with other third-party organizations for their marketing or promotional use without your consent or except as
        part of a specific program or feature for which you will have the ability to opt-in.
      </p>

      <p className="mb-7">
        We maintain written contracts with these third parties and require that these third parties provide at least the same
        level of privacy protection and security as required by the EU-U.S. Data Privacy Framework (“EU-U.S. DPF”) and the
        Swiss-U.S. Data Privacy Framework (“Swiss-U.S. DPF” and together with the EU-U.S. DPF, the “DPFs”). To the extent provided
        by the DPFs, Outplayed remains responsible and liable under the DPFs if a third-party that it engages to process Personal
        Information on its behalf does so in a manner inconsistent with the DPFs, unless Outplayed proves that it is not
        responsible for the matter giving rise to the damage.
      </p>

      <p className="mb-7">
        Outplayed, and its associated websites, U.GG, probuildstats.com, comply with the DPFs as set forth by the U.S. Department
        of Commerce. Outplayed, Inc. has certified to the U.S. Department of Commerce that it adheres to the EU-U.S. DPF with
        regard to the processing of personal data received from the European Union in reliance on the EU-U.S. DPF. Outplayed, Inc.
        has certified to the U.S. Department of Commerce that it adheres to the Swiss-U.S. DPF with regard to the processing of
        personal data received from Switzerland in reliance on the Swiss-U.S. DPF. If there is any conflict between the terms in
        this privacy policy and the DPFs, the DPFs shall govern. To learn more about the DPF program, and to view our
        certification, please visit{" "}
        <a target="_blank" href="https://www.dataprivacyframework.gov/">
          https://www.dataprivacyframework.gov/
        </a>
        .
      </p>

      <h2 id="section5">5. Communications</h2>
      <p className="mb-7">U.GG may need to communicate with you for a variety of different reasons, including:</p>

      <ul className="mb-7">
        <li>
          Responding to your questions and requests. If you contact us with a problem or question, we will use your information to
          respond.
        </li>
        <li>
          Sending you Service and administrative emails and messages. We may contact you to inform you about changes in our
          Services, our Service offerings, and important Service related notices, such as billing, security and fraud notices.
          These emails and messages are considered a necessary part of the Services and you may not opt-out of them.
        </li>
        <li>
          Sending emails about new products or other news about U.GG that we think you'd like to hear about either from us or from
          our business partners. You can always opt out of these types of messages at any time by clicking the unsubscribe link at
          the bottom of each communication.
        </li>
        <li>
          Conducting surveys. We may use the information gathered in the surveys to enhance and personalize our products,
          services, and websites.
        </li>
        <li>
          Offering referral programs and incentives, which allow you to utilize email, text, or URL links that you can share with
          friends or colleagues.
        </li>
      </ul>

      <h2 id="section6">6. Accessing Your Data</h2>
      <p className="mb-7">
        U.GG will, when you request, provide you with information about whether we hold, or process on behalf of a third party,
        any of your Personal Information. Please contact us at <a href="mailto:privacy@u.gg">privacy@u.gg</a> if you need
        assistance in reviewing your information. U.GG will respond to your access request to review the information we have on
        file for you within a reasonable time.
      </p>

      <p className="mb-7">
        Pursuant to the DPFs, EU, and Swiss individuals have the right to obtain our confirmation of whether we maintain Personal
        Information relating to you in the United States. Upon request, we will provide you with access to the Personal
        Information that we hold about you. You may also correct, amend, or delete the Personal Information we hold about you. An
        individual who seeks access, or who seeks to correct, amend, or delete inaccurate data transferred to the United States
        under the DPFs, should direct their query to <a href="mailto:privacy@u.gg">privacy@u.gg</a>. If requested to remove data,
        we will respond within a reasonable timeframe.
      </p>

      <p className="mb-7">
        We may transfer Personal Information to companies that help us provide our service, and when we do, these transfers to
        subsequent third parties are covered by appropriate transfer agreements. We will retain personal data we process on behalf
        of our customer as needed to provide services to our customers. Also, we will retain this Personal Information as
        necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
      </p>

      <p className="mb-7">
        U.GG is headquartered in the United States of America and maintains a global infrastructure. Information that we collect
        and maintain may be transferred to, or controlled and processed in, the United States and/or other countries around the
        world. When you provide us with information, or use our website(s) and services, you consent to this transfer. We will
        protect the privacy and security of Personal Information we collect in accordance with this privacy policy, regardless of
        where it is processed or stored.
      </p>

      <h2 id="section7">7. Security</h2>
      <p className="mb-7">
        U.GG follows generally accepted standards to protect the Personal Information submitted to us, both during transmission
        and once it is received, however, no security measure is perfect. We recommend safeguarding your password, as it is one of
        the easiest ways you can manage the security of your own account – remember that if you lose control over your password,
        you may lose control over your Personal Information.
      </p>

      <h2 id="section8">8. Data Retention</h2>
      <p className="mb-7">
        We retain the Personal Information we receive as described in this Privacy Policy for as long as you use our Services or
        as necessary to fulfil the purpose(s) for which it was collected, provide our Services, resolve disputes, establish legal
        defenses, conduct audits, pursue legitimate business purposes, enforce our agreements, and comply with applicable laws.
        This length of time will vary depending on the Service and the nature of the information and may extend beyond the end of
        your relationship with us.
      </p>

      <p className="mb-7">
        In determining this, we will be considerate of the amount, nature and sensitivity of the Personal Information, the
        potential risk of harm to the Personal Information, and whether our purposes can be achieved through other means.
      </p>

      <h2 id="section9">9. Providing or Withdrawing your Consent</h2>
      <p className="mb-7">
        We will obtain your consent before collecting, using or disclosing your Personal Information, except where permitted or
        required by law. Depending on the situation and the sensitivity of the information, we may obtain your consent in
        different ways. Express consent may be obtained verbally, electronically or in writing from you or your authorized
        representative (such as a legal guardian or attorney appointed pursuant to a power of attorney) for stated purpose(s).
        Implied consent may be obtained through your use or continued use of a Services when the purpose(s) for collecting, using
        or disclosing your Personal Information is indicated by the relevant circumstances or follows logically from purposes
        identified in this Privacy Policy. We will seek your consent before using your Personal Information for any purpose not
        previously identified and will limit the collection of your Personal Information to that which is reasonably necessary for
        the relevant purpose(s). We will not, as a condition of the supply of any of the Services, require you to consent to the
        collection, use or disclosure of Personal Information beyond that which is reasonably required to fulfill the purpose for
        which it is collected.
      </p>

      <p className="mb-7">
        In most cases you may refuse to provide your consent to our collection, use and/or disclosure of your Personal Information
        in accordance with this Privacy Policy. Note, however, that if you refuse to provide your consent or withdraw your
        consent, we may not be able to provide you with a particular Service.
      </p>

      <p className="mb-7">
        You may withdraw your consent provided that: you provide reasonable notice; we are not legally required to collect, use or
        disclose your Personal Information; and withdrawing your consent does not impede our ability to fulfill our obligations to
        you. You may withdraw your consent by contacting us at <a href="mailto:privacy@u.gg">privacy@u.gg</a>.
      </p>

      <h2 id="section10">10. Internal Policies and Procedures</h2>
      <p className="mb-7">
        We train and educate every employee about the above Privacy Policy and any changes that may occur. Employees that are
        found to violate the Privacy Policy face severe disciplinary actions - which can include termination and a referral to law
        enforcement agencies in the most extreme cases. Employees are trained upon employment and at least once every calendar
        year, and the privacy policies are reviewed at least once every calendar year. A statement verifying the above is signed
        at least once per year and available upon request.
      </p>

      <h2 id="section11">11. Our Commitment to Children's Privacy</h2>
      <p className="mb-7">
        Our Services are not mainly for minors and our website does not knowingly collect any Personal Information from minors. If
        a User is deemed as minor under applicable law, please make sure that before the use of the application which integrates
        our Services or the application of an advertiser that partners with us for delivering its ads, parents or other guardian
        and such User have together read the application’s privacy policy with the guardianship and guiding of parents or other
        guardian, and their consent have been provided for processing of Personal Information.
      </p>

      <p className="mb-7">
        We process minors’ Personal Information according to requirements of related laws and regulations. Except as otherwise
        provided by law, we will only collect, use, store, disclose, or make available to the public the Personal Information
        where permitted by law, with consent of parents or other guardians, or for the necessity of protection of the minors. If
        we notice the collection of Personal Information of minors without verifiable consent of parents, we will take measures to
        delete related information as soon as possible.
      </p>

      <p className="mb-7">
        If you are parents or other guardian of minors, please contact us at <a href="mailto:privacy@u.gg">privacy@u.gg</a> when
        you have any question regarding to the protection of minors.
      </p>

      <p className="mb-7">
        If a User is in the U.S., we ensure that we comply with the Children’s Online Privacy Protection Act (COPPA). If our
        business partners operate applications directed to children under the age of thirteen (13) or knowingly collect, use or
        disclose Personal Information of children under the age of thirteen (13) during our cooperation, they will obtain
        verifiable parental consent for us to collect, use or disclose such Personal Information according to this Privacy Policy.
        Further, such business partners must enable the COPPA settings when using our Services. This allows us to ensure that any
        persistent identifiers collected about Users of applications that are flagged as “child-directed” will be processed
        according to the requirements of COPPA, including not being used to serve behaviorally-targeted ads or track users for
        such purposes in violation of COPPA. In addition, we will not knowingly collect any other Personal Information (such as
        the precise geo-location of Users’ devices) from Users that we know to be directed to children under the age of thirteen
        (13).
      </p>

      <p className="mb-7">
        If a parent or guardian becomes aware that his or her child has provided us with information without their consent, he or
        she should promptly contact us at <a href="mailto:privacy@u.gg">privacy@u.gg</a> and we will take reasonable steps to
        ensure that such information is deleted from our files.
      </p>

      <h2 id="section12">12. EU-U.S. Data Privacy Framework</h2>
      <p className="mb-7">
        Outplayed and its associated websites (u.gg, probuildstats.com) comply with the EU-U.S. Data Privacy Framework (EU-U.S.
        DPF) and the Swiss-U.S. Data Privacy Framework (Swiss-U.S. DPF), as set forth by the U.S. Department of Commerce
        (collectively, the DPFs) regarding the collection, use, and retention of Personal Information transferred from 1) the
        European Union (EU) to the United States in reliance on the EU-U.S. DPF, and 2) Switzerland to the United States in
        reliance on the Swiss-U.S. DPF. Outplayed has certified that it adheres to the DPFs with respect to such data. If there is
        any conflict between the policies in this privacy policy and data subject rights under the DPFs, the DPFs shall govern. To
        learn more about the DPFs, and to view our certification page, please visit
        <a href=" https://www.dataprivacyframework.gov/"> https://www.dataprivacyframework.gov/</a>.
      </p>

      <p className="mb-7">
        Outplayed is subject to the investigatory and enforcement powers of the U.S. Federal Trade Commission (“FTC”).{" "}
      </p>

      <p className="mb-7">
        Pursuant to the DPFs, EU, and Swiss individuals have the right to obtain our confirmation of whether we maintain Personal
        Information relating to you in the United States. Upon request, we will provide you with access to the Personal
        Information that we hold about you. You may also correct, amend, or delete the Personal Information we hold about you. An
        individual who seeks access, or who seeks to correct, amend, or delete inaccurate data transferred to the United States
        under the DPFs, should direct their query to <a href="mailto:privacy@u.gg">privacy@u.gg</a>. If requested to remove data,
        we will respond within a reasonable timeframe. See Section 6 of this Privacy Policy “Accessing Your Data” for more
        information.
      </p>

      <p className="mb-7">
        We will provide an individual opt-out choice, or opt-in for sensitive data, before we share your data with third parties
        other than our agents, or before we use it for a purpose other than which it was originally collected or subsequently
        authorized. To request to limit the use and disclosure of your Personal Information, please submit a written request to{" "}
        <a href="mailto:privacy@u.gg">privacy@u.gg</a>.
      </p>

      <p className="mb-7">
        In certain situations, we may be required to disclose personal data in response to lawful requests by public authorities,
        including to meet national security or law enforcement requirements.
      </p>

      <p className="mb-7">
        If you are an EU or Swiss individual, where we transfer your personal data to third party service providers who perform
        services for us or on our behalf, we are responsible for the processing of that data by them and shall remain liable if
        they process your personal data in a manner inconsistent with the DPF Principles, unless we prove that we are not
        responsible for the event giving rise to the damage. See Section 4 of this Privacy Policy “Information Sharing and
        Accountability for Onward Transfers” for more information.
      </p>

      <p className="mb-7">
        In compliance with the DPFs, Outplayed commits to resolve complaints about your privacy and our collection or use of your
        Personal Information transferred to the United States pursuant to the DPFs. EU and Swiss individuals with DPF inquiries or
        complaints should first contact Outplayed by email at <a href="mailto:privacy@u.gg">privacy@u.gg</a>.
      </p>

      <p className="mb-7">
        In compliance with the DPFs, Outplayed, Inc. commits to refer unresolved complaints concerning our handling of personal
        data received in reliance on the DPFs to BBB National Programs, an alternative dispute resolution provider based in the
        United States. If you do not receive timely acknowledgment of your DPF-related complaint from us, or if we have not
        addressed your DPF-related complaint to your satisfaction, please visit{" "}
        <a href="https://bbbprograms.org/programs/all-programs/dpf-consumers">
          https://bbbprograms.org/programs/all-programs/dpf-consumers
        </a>{" "}
        for more information or to file a complaint. This service is provided free of charge.
      </p>

      <p className="mb-7">
        If your DPF complaint cannot be resolved through the above channels, under certain conditions, you may invoke binding
        arbitration for some residual claims not resolved by other redress mechanisms See{" "}
        <a href="https://www.dataprivacyframework.gov/framework-article/ANNEX-I-introduction">
          https://www.dataprivacyframework.gov/framework-article/ANNEX-I-introduction/
        </a>{" "}
        for more information on this process.
      </p>

      <h2 id="section13">13. Additional Data Rights</h2>
      <p className="mb-7">
        You have the right to access, modify, correct or delete any personal data which we may have collected. To exercise this
        right please contact privacy <a href="mailto:privacy@u.gg">privacy@u.gg</a>.
      </p>

      <h3 className="leading-7">Additional Data Rights Applicable to EEA Residents</h3>
      <p className="mb-7">
        This section of this Privacy Policy is applicable to residents of the EEA. This section also applies to residents of
        Switzerland and of the United Kingdom. Residents of the EEA, United Kingdom and Switzerland are referred to here as “EEA
        Residents.”
      </p>

      <p className="mb-7">
        All processing of Personal Information of EEA Residents is performed by us in accordance with the privacy laws applicable
        in those jurisdictions including the General Data Protection Regulation (2016/679) of the European Parliament and of the
        Council of 27 April 2016 on the protection of natural persons regarding the processing of Personal Information and on the
        free movement of such data (“GDPR”) and the GDPR as it is enacted in the UK.
      </p>

      <p className="mb-7">
        All processing of Personal Information of EEA Residents is performed by us in accordance with the privacy laws applicable
        in those jurisdictions including the General Data Protection Regulation (2016/679) of the European Parliament and of the
        Council of 27 April 2016 on the protection of natural persons regarding the processing of Personal Information and on the
        free movement of such data (“GDPR”) and the GDPR as it is enacted in the UK.
      </p>

      <p className="mb-7">
        Under applicable law, Outplayed is the controller of the Personal Information of EEA Residents. If you would like to
        contact us, please use the contact details in this policy.
      </p>

      <p className="mb-7">
        We only process your Personal Information where it is lawful to do so and in accordance with the purposes described above.
        When doing so, we rely on the following lawful bases:
      </p>
      <ul className="mb-7">
        <li>
          Consent: we may seek your consent to process your Personal Information, for example to send you marketing
          communications.
        </li>
        <li>
          Contract: we may process your Personal Information when necessary for the performance of the contract that we are party
          to with you, for example, the terms and conditions you may accept when using our Services.
        </li>
        <li>
          Legal obligation: we may process your Personal Information when we consider ourselves to be subject to a legal
          obligation, for example if we are required to respond to a request from an authority.
        </li>
        <li>
          Legitimate interests: we may process your Personal Information when we have a legitimate interest do so and this does
          not override your rights and freedoms. Our legitimate interests are securing our sites, Services, property, systems, and
          users, improving our sites and Services, growing and improving our business and customer relationships, marketing and
          advertising, resolving disputes, and preventing fraud and abuse.
        </li>
      </ul>

      <p className="mb-7">As an EEA Resident, you may have the following rights with regards your Personal Information:</p>
      <ul className="mb-7">
        <li>
          The right to request access to, and a copy of, the Personal Information we hold about you, including details as to how
          we process this Personal Information.
        </li>
        <li>The right to object to, or restrict, our processing of your Personal Information.</li>
        <li>The right to request that your Personal Information be erased from further use.</li>
        <li>
          The right to correct, amend, or update Personal Information you have given us (in some instances, where you have an
          account with us, you can also do this by logging in and updating your Personal Information).
        </li>
        <li>The right of portability, which allows you to request that we share your Personal Information with others.</li>
        <li>The right to withdraw any consent to processing you have provided us.</li>
      </ul>

      <p className="mb-7">
        At all times, you also have the right to submit a complaint to a data protection regulator in the EEA, UK or Switzerland
      </p>

      <h3 className="leading-7">Additional Data Rights Applicable to California Residents</h3>
      <p className="mb-7">
        The following section pertains to the rights of individuals or households in California ("California consumers").
      </p>

      <p className="mb-7">
        Under certain circumstances, California Civil Code Section 1798.83 states that, upon receipt of a request by a California
        consumer, a business may be required to provide detailed information regarding how that business has shared that
        customer’s Personal Information with third parties for direct marketing purposes. However, the foregoing does not apply to
        businesses like ours that do not disclose Personal Information to third parties for direct marketing purposes without
        prior approval or give customers a free mechanism to opt out of having their Personal Information disclosed to third
        parties for their direct marketing purposes.
      </p>

      <p className="mb-7">
        The CCPA (California Civil Code Section 1798.100 et seq.) provides California consumers with additional rights regarding
        Personal Information that identifies, relates to, describes, is capable of being associated with, or could reasonably be
        linked, directly or indirectly, with a particular consumer or household. The categories of Personal Information we collect
        are generally described above but differ for individual consumers depending on the Services used by such consumers.
      </p>

      <p className="mb-7">
        Right to Know and Right to Delete: A California consumer has the right to request that we disclose what Personal
        Information we collect, use, disclose and sell. A California consumer also has the right to submit requests to delete
        Personal Information. When we receive a request to know or delete from a California consumer, we will confirm receipt of
        the request within 10 business days and provide information about how we will process the request, including our
        verification process. We will respond to such requests within 45 days.
      </p>

      <p className="mb-7">
        Right to Correct: Under the CPRA, effective January 1, 2023, California residents have the right to request that we
        correct any inaccurate Personal Information we maintain about you, taking into account the nature of the Personal
        Information and the purposes for which we are processing such Personal Information. We will use commercially reasonable
        efforts to correct such inaccurate Personal Information about you.
      </p>

      <p className="mb-7">
        Right to Opt-Out of Sale or Sharing: Under the CCPA, California residents have the right to opt-out of the sale of their
        Personal Information. Please note we do not sell Personal Information and we do not knowingly sell the Personal
        Information of any individuals under the age of 16. Under the CPRA, which becomes effective on January 1, 2023, California
        residents also have the right to opt-out of the sharing of their Personal Information. As mentioned above, we share your
        Personal Information with our service providers, our business partners, and with legal/regulatory authorities when we are
        required to do so. If you have any questions regarding your rights to opt out of the sharing of your Personal Information,
        please contact us at <a href="mailto:privacy@u.gg">privacy@u.gg</a>. We do not engage in cross-context behavioral
        advertising or profiling.
      </p>

      <p className="mb-7">
        Right for Disclosure of Personal Information: A California consumer may also submit requests that we disclose specific
        types or categories of Personal Information that we collect.
      </p>

      <p className="mb-7">
        Under certain circumstances, we will not provide such Personal Information, including where the disclosure creates a
        substantial, articulable and unreasonable risk to the security of that Personal Information, customers’ accounts with us,
        or the security of our systems or networks. We also will not disclose California consumers’ social security numbers,
        driver’s license numbers or other government-issued identification numbers, financial account numbers, any health
        insurance or medical identification numbers, or account passwords and security questions and answers.
      </p>

      <p className="mb-7">
        If you are a California consumer and would like to make any requests under the CCPA, please direct them as follows:
        Outplayed, Inc., Attention: Privacy Officer, 2 St. Clair Ave W, Suite 1000 Toronto, ON M4V 1L5; or at{" "}
        <a href="mailto:privacy@u.gg">privacy@u.gg</a>.
      </p>

      <p className="mb-7">
        If we receive any request, we will use a two-step process for online requests where the California consumer must first,
        clearly submit the request and then second, separately confirm the request. We will use other appropriate measures to
        verify requests received by mail or telephone.
      </p>

      <p className="mb-7">
        In submitting a request, a California consumer must provide sufficient information to identify the consumer, such as name,
        email address, home or work address, or other such information that is on record with us so that we can match such
        information to the Personal Information that we maintain. Do not provide social security numbers, driver’s license
        numbers, account numbers, credit or debit card numbers, medical information or health information with requests. If
        requests are unclear or submitted through means other than as outlined above, we will provide the California consumer with
        specific directions on how to submit the request or remedy any deficiencies. If we cannot verify the identity of the
        requestor, we may deny the request.
      </p>

      <h2 id="section14">14. Changes to this Statement/Contact Us</h2>
      <p className="mb-7">
        We may update this Privacy Policy to reflect changes to our information practices. If we make any material changes we will
        provide notice on this website, and we may notify you by email (sent to the e-mail address specified in your account),
        prior to the change becoming effective.
      </p>

      <p className="mb-7">
        We encourage you to periodically review this page for the latest information on our privacy practices. If you continue to
        use the Services after those changes are in effect, you agree to the revised policy.
      </p>

      <p className="mb-7">
        If you have any other questions about this policy please contact the U.GG Privacy Team via email at{" "}
        <a href="mailto:privacy@u.gg">privacy@u.gg</a>, or write to us via postal mail at: U.GG, 200 E. 6th St Suite 202, Austin,
        TX, 78701, USA. To reach our Global Customer Support department, you may contact us via email at{" "}
        <a href="mailto:info@u.gg">info@u.gg</a>.
      </p>
    </div>
  );
};
